import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Device } from '@ionic-native/device/ngx';
import { SettingsService } from '../services/settings.service';
import { TenantAuthService } from '../services/tenant-auth.service';
import { CompanyStorage } from './company-storage';

export const STORAGE_EXPORT_FORMAT = 'pos-terminal-company-storage/1';

export interface StorageExportResult {
  container: string;
  name: string;
  size: number;
  keys: number;
  parts: number;
  readMs: number;
  uploadMs: number;
}

export interface StorageKeySummary {
  key: string;
  type: string;
  items?: number;
  bytes: number;
}

// One part per request: the nginx in front of Nt.Pos.Api takes 1 MB, the base64 JSON body stays under it.
const PART_BYTES = 480 * 1024;

// Reads the whole company storage (every key and value, secrets included: the admin has full
// control) and uploads it as one JSON file to Nt.Pos.Api in parts; it lands in the device's blob
// container for the admin tool.
@Injectable({ providedIn: 'root' })
export class PosTerminalStorageExportService {
  constructor(
    private _http: HttpClient,
    private _settings: SettingsService,
    private _device: Device,
    private _companyStorage: CompanyStorage,
    private _tenantAuth: TenantAuthService,
    @Inject('ENVIRONMENT') private _environment: { version?: string },
  ) {}

  async upload(commandId?: string): Promise<StorageExportResult> {
    // CompanyStorage alerts on the terminal screen without a company: refuse before touching it.
    const tenant = this._tenantAuth.currentTenant;
    if (!tenant?.clientid) {
      throw new Error('No company is selected on this terminal: there is no company storage');
    }

    const readStarted = performance.now();
    const { document, keys } = await this.read(tenant.clientid, tenant.name);
    const bytes = new TextEncoder().encode(JSON.stringify(document));
    const readMs = Math.round(performance.now() - readStarted);

    const uploadStarted = performance.now();
    const base = `${this._settings.pos_api_endpoint}/terminal/${encodeURIComponent(this._device.uuid)}/files`;
    const params = new HttpParams().set('responseFormater', 'none');

    const started = await this._http
      .post<{ name: string }>(base, { kind: 'storage' }, { params })
      .toPromise();

    const parts = Math.max(1, Math.ceil(bytes.length / PART_BYTES));
    for (let index = 0; index < parts; index++) {
      const part = bytes.subarray(index * PART_BYTES, (index + 1) * PART_BYTES);
      await this._http
        .put(
          `${base}/blocks`,
          { dataBase64: this._toBase64(part) },
          { params: params.set('name', started.name).set('index', String(index)) },
        )
        .toPromise();
    }

    const saved = await this._http
      .post<{ container: string; name: string; size: number }>(
        `${base}/commit`,
        {
          name: started.name,
          blockCount: parts,
          contentType: 'application/json',
          commandId,
          metadata: {
            keys: String(keys.length),
            format: STORAGE_EXPORT_FORMAT,
          },
        },
        { params },
      )
      .toPromise();

    return {
      container: saved.container,
      name: saved.name,
      size: saved.size,
      keys: keys.length,
      parts,
      readMs,
      uploadMs: Math.round(performance.now() - uploadStarted),
    };
  }

  // The export document: a summary per key and the data. include: only the keys it accepts.
  async read(tenantId: string, tenantName?: string, include?: (key: string) => boolean) {
    const keys = ((await this._companyStorage.keys()) || []).filter((key) => !include || include(key)).sort();
    const data: Record<string, unknown> = {};
    const summary: StorageKeySummary[] = [];

    for (const key of keys) {
      const raw = await this._companyStorage.get(key);
      const value = this.toJsonSafe(raw);
      data[key] = value;
      summary.push({
        key,
        type: this._typeOf(raw),
        items: Array.isArray(raw) ? raw.length : undefined,
        bytes: new TextEncoder().encode(JSON.stringify(value) ?? '').length,
      });
    }

    const document = {
      format: STORAGE_EXPORT_FORMAT,
      deviceId: this._device.uuid,
      platform: this._device.platform,
      tenantId,
      tenantName: tenantName || null,
      appVersion: this._environment?.version || null,
      createdAtUtc: new Date().toISOString(),
      keys: summary,
      data,
    };

    return { document, keys: summary };
  }

  // JSON-safe copy of a stored value: binary values are described instead of lost, dates become
  // ISO strings. getStorage and its hash use the same form, so they match the export.
  toJsonSafe(value: unknown, depth = 0): unknown {
    if (value === null || value === undefined || this._isPrimitive(value)) {
      return value === undefined ? null : value;
    }

    if (depth > 64) {
      return '[too deep]';
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      return { $type: 'Blob', size: value.size, contentType: value.type };
    }

    if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
      return { $type: value.constructor?.name || 'ArrayBuffer', byteLength: (value as ArrayBuffer).byteLength };
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.toJsonSafe(item, depth + 1));
    }

    const copy: Record<string, unknown> = {};
    for (const [name, item] of Object.entries(value as Record<string, unknown>)) {
      copy[name] = this.toJsonSafe(item, depth + 1);
    }

    return copy;
  }

  // True when the value holds something JSON can not carry (a Blob, a buffer): writing an edited
  // copy back would replace it with its description.
  hasBinary(value: unknown, depth = 0): boolean {
    if (value === null || typeof value !== 'object' || depth > 64) {
      return false;
    }

    if ((typeof Blob !== 'undefined' && value instanceof Blob) || value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
      return true;
    }

    const items = Array.isArray(value) ? value : Object.values(value as Record<string, unknown>);
    return items.some((item) => this.hasBinary(item, depth + 1));
  }

  private _isPrimitive(value: unknown): boolean {
    return ['string', 'number', 'boolean', 'bigint'].includes(typeof value);
  }

  private _typeOf(value: unknown): string {
    if (value === null || value === undefined) {
      return 'null';
    }

    return Array.isArray(value) ? 'array' : typeof value;
  }

  private _toBase64(bytes: Uint8Array): string {
    let binary = '';
    const step = 0x8000;
    for (let i = 0; i < bytes.length; i += step) {
      binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + step)));
    }

    return btoa(binary);
  }
}
